import { IApplyCourse, StudyLevel, StudyMode, FeeStatus, EntryPoint } from 'types';
import { CourseOptionWithMetadata } from 'shared-components';
import { DropdownOption, CoursesMap } from '../types';

/**
 * Represents an aggregated course with all its variants
 */
export interface AggregatedCourse {
  name: string;
  modes: Set<StudyMode>;
  campuses: Set<string>;
  entryPoints: Set<EntryPoint>;
  courseIds: string[];
}

/**
 * Organizes courses by study level and mode for efficient lookup
 */
export const organizeCoursesByLevelAndMode = (courses: IApplyCourse[]): CoursesMap => {
  const coursesMap = new Map<string, IApplyCourse[]>();

  for (const course of courses) {
    const key = `${course.level}-${course.mode}`;
    if (!coursesMap.has(key)) {
      coursesMap.set(key, []);
    }
    coursesMap.get(key)!.push(course);
  }

  return coursesMap;
};

/**
 * Gets available modes for a specific study level
 */
export const getModesForStudyLevel = (courses: IApplyCourse[], studyLevel: StudyLevel): DropdownOption[] => {
  const modesSet = new Set<StudyMode>();

  for (const course of courses) {
    if (course.level === studyLevel) {
      modesSet.add(course.mode);
    }
  }

  return Array.from(modesSet)
    .map(mode => ({
      value: mode,
      label: formatModeLabel(mode),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Formats study mode for display
 */
export const formatModeLabel = (mode: StudyMode): string => {
  const modeLabels: Record<StudyMode, string> = {
    [StudyMode.FullTime]: 'Full-time',
    [StudyMode.PartTime]: 'Part-time',
    [StudyMode.MixedMode]: 'Mixed mode',
    [StudyMode.Sandwich]: 'Sandwich',
    [StudyMode.DistanceLearning]: 'Distance learning',
  };

  return modeLabels[mode] || mode;
};

/**
 * Filters intakes based on fee status
 */
export const filterIntakesByFeeStatus = (
  intakes: DropdownOption[],
  courses: IApplyCourse[],
  feeStatus: FeeStatus | string,
): DropdownOption[] => {
  if (!feeStatus || courses.length === 0) {
    return intakes;
  }

  // Treat "Not sure" as "Overseas"
  const effectiveFeeStatus = feeStatus === FeeStatus.NotSure ? FeeStatus.Overseas : feeStatus;

  // Get intakes that have courses available for the selected fee status
  const availableIntakeIds = new Set<string>();

  for (const course of courses) {
    const isAvailable = effectiveFeeStatus === FeeStatus.Home ? course.availableHome : course.availableOverseas;

    if (isAvailable) {
      availableIntakeIds.add(course.intakeId);
    }
  }

  return intakes.filter(intake => availableIntakeIds.has(intake.value));
};

/**
 * Filters intakes based on available courses and fee status
 */
export const filterIntakesByCoursesAndFeeStatus = (
  intakes: DropdownOption[],
  courses: IApplyCourse[],
  feeStatus: FeeStatus | string,
): DropdownOption[] => {
  if (!feeStatus || courses.length === 0) {
    return intakes;
  }

  // Treat "Not sure" as "Overseas"
  const effectiveFeeStatus = feeStatus === FeeStatus.NotSure ? FeeStatus.Overseas : feeStatus;

  // Get intakes that have courses available for the selected fee status
  const availableIntakeIds = new Set<string>();

  for (const course of courses) {
    const isAvailable = effectiveFeeStatus === FeeStatus.Home ? course.availableHome : course.availableOverseas;

    if (isAvailable) {
      availableIntakeIds.add(course.intakeId);
    }
  }

  return intakes.filter(intake => availableIntakeIds.has(intake.value));
};

/**
 * Gets available study levels from courses
 */
export const getAvailableStudyLevels = (courses: IApplyCourse[]): DropdownOption[] => {
  const availableStudyLevels = new Set<StudyLevel>();

  for (const course of courses) {
    availableStudyLevels.add(course.level as StudyLevel);
  }

  return Array.from(availableStudyLevels).map(level => ({
    value: level,
    label: level,
  }));
};

/**
 * Gets available study levels for a specific intake and fee status
 */
export const getAvailableStudyLevelsForIntake = (
  courses: IApplyCourse[],
  intakeId: string,
  feeStatus: FeeStatus | string,
): DropdownOption[] => {
  const effectiveFeeStatus = feeStatus === FeeStatus.NotSure ? FeeStatus.Overseas : feeStatus;
  const availableStudyLevels = new Set<StudyLevel>();

  for (const course of courses) {
    if (course.intakeId !== intakeId) continue;

    const isAvailable = effectiveFeeStatus === FeeStatus.Home ? course.availableHome : course.availableOverseas;
    if (isAvailable) {
      availableStudyLevels.add(course.level as StudyLevel);
    }
  }

  return Array.from(availableStudyLevels)
    .map(level => ({
      value: level,
      label: level,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Gets available modes for a specific intake, fee status, and study level
 */
export const getModesForIntakeAndStudyLevel = (
  courses: IApplyCourse[],
  intakeId: string,
  feeStatus: FeeStatus | string,
  studyLevel: StudyLevel,
): DropdownOption[] => {
  const effectiveFeeStatus = feeStatus === FeeStatus.NotSure ? FeeStatus.Overseas : feeStatus;
  const modesSet = new Set<StudyMode>();

  for (const course of courses) {
    if (course.intakeId !== intakeId || course.level !== studyLevel) continue;

    const isAvailable = effectiveFeeStatus === FeeStatus.Home ? course.availableHome : course.availableOverseas;
    if (isAvailable) {
      modesSet.add(course.mode);
    }
  }

  return Array.from(modesSet)
    .map(mode => ({
      value: mode,
      label: formatModeLabel(mode),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Gets available courses for selected criteria
 */
export const getAvailableCourses = (
  coursesMap: CoursesMap,
  studyLevel: StudyLevel | string,
  mode: StudyMode | string,
): DropdownOption[] => {
  if (!studyLevel || !mode) {
    return [];
  }

  const key = `${studyLevel}-${mode}`;
  const courses = coursesMap.get(key) || [];

  return courses
    .map(course => ({
      value: course.id,
      label: course.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Gets courses for the full selection criteria
 */
export const getCoursesForSelection = (
  courses: IApplyCourse[],
  intakeId: string,
  feeStatus: FeeStatus | string,
  studyLevel: StudyLevel | string,
  mode: string,
): DropdownOption[] => {
  const effectiveFeeStatus = feeStatus === FeeStatus.NotSure ? FeeStatus.Overseas : feeStatus;
  const filteredCourses: IApplyCourse[] = [];

  for (const course of courses) {
    if (course.intakeId === intakeId && course.level === studyLevel && course.mode === mode) {
      const isAvailable = effectiveFeeStatus === FeeStatus.Home ? course.availableHome : course.availableOverseas;
      if (isAvailable) {
        filteredCourses.push(course);
      }
    }
  }

  return filteredCourses
    .map(course => ({
      value: course.id,
      label: course.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Aggregates courses by name for display in dropdown
 * Returns a map of course name to aggregated course data
 */
export const aggregateCoursesByName = (courses: IApplyCourse[]): Map<string, AggregatedCourse> => {
  const aggregated = new Map<string, AggregatedCourse>();

  for (const course of courses) {
    if (!aggregated.has(course.name)) {
      aggregated.set(course.name, {
        name: course.name,
        modes: new Set(),
        campuses: new Set(),
        entryPoints: new Set(),
        courseIds: [],
      });
    }

    const agg = aggregated.get(course.name)!;
    agg.modes.add(course.mode);
    agg.campuses.add(course.campus);
    agg.entryPoints.add(course.entryPoint);
    agg.courseIds.push(course.id);
  }

  return aggregated;
};

/**
 * Formats modes for display in course option metadata
 */
export const formatModesForDisplay = (modes: StudyMode[]): string => {
  return modes.map(mode => formatModeLabel(mode)).join(', ');
};

/**
 * Formats campuses for display in course option metadata
 */
export const formatCampusesForDisplay = (campuses: string[]): string => {
  if (campuses.length === 1) return campuses[0];
  return `${campuses.length} campuses`;
};

/**
 * Gets aggregated courses for selection (courses grouped by name)
 * Returns course names as values instead of individual course IDs
 */
export const getAggregatedCoursesForSelection = (
  courses: IApplyCourse[],
  intakeId: string,
  feeStatus: FeeStatus | string,
  studyLevel: StudyLevel | string,
): CourseOptionWithMetadata[] => {
  const effectiveFeeStatus = feeStatus === FeeStatus.NotSure ? FeeStatus.Overseas : feeStatus;
  const filteredCourses: IApplyCourse[] = [];

  for (const course of courses) {
    if (course.intakeId === intakeId && course.level === studyLevel) {
      const isAvailable = effectiveFeeStatus === FeeStatus.Home ? course.availableHome : course.availableOverseas;
      if (isAvailable) {
        filteredCourses.push(course);
      }
    }
  }

  const aggregated = aggregateCoursesByName(filteredCourses);

  return Array.from(aggregated.values())
    .map(course => ({
      value: course.name,
      label: course.name,
      metadata: {
        modes: formatModesForDisplay(Array.from(course.modes)),
        campuses: formatCampusesForDisplay(Array.from(course.campuses)),
      },
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Gets available modes for a specific course name
 */
export const getModesForCourseName = (
  courses: IApplyCourse[],
  intakeId: string,
  feeStatus: FeeStatus | string,
  studyLevel: StudyLevel | string,
  courseName: string,
): DropdownOption[] => {
  const effectiveFeeStatus = feeStatus === FeeStatus.NotSure ? FeeStatus.Overseas : feeStatus;
  const modesSet = new Set<StudyMode>();

  for (const course of courses) {
    if (course.intakeId === intakeId && course.level === studyLevel && course.name === courseName) {
      const isAvailable = effectiveFeeStatus === FeeStatus.Home ? course.availableHome : course.availableOverseas;
      if (isAvailable) {
        modesSet.add(course.mode);
      }
    }
  }

  return Array.from(modesSet)
    .map(mode => ({
      value: mode,
      label: formatModeLabel(mode),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Gets available campuses for a specific course name and mode
 */
export const getCampusesForCriteria = (
  courses: IApplyCourse[],
  intakeId: string,
  feeStatus: FeeStatus | string,
  studyLevel: StudyLevel | string,
  courseName: string,
  mode: string,
): DropdownOption[] => {
  const effectiveFeeStatus = feeStatus === FeeStatus.NotSure ? FeeStatus.Overseas : feeStatus;
  const campusSet = new Set<string>();

  for (const course of courses) {
    if (
      course.intakeId === intakeId &&
      course.level === studyLevel &&
      course.name === courseName &&
      course.mode === mode
    ) {
      const isAvailable = effectiveFeeStatus === FeeStatus.Home ? course.availableHome : course.availableOverseas;
      if (isAvailable) {
        campusSet.add(course.campus);
      }
    }
  }

  return Array.from(campusSet)
    .map(campus => ({
      value: campus,
      label: campus,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Gets available entry years for a specific course name, mode, and campus
 */
export const getEntryPointsForCriteria = (
  courses: IApplyCourse[],
  intakeId: string,
  feeStatus: FeeStatus | string,
  studyLevel: StudyLevel | string,
  courseName: string,
  mode: string,
  campus: string,
): DropdownOption[] => {
  const effectiveFeeStatus = feeStatus === FeeStatus.NotSure ? FeeStatus.Overseas : feeStatus;
  const entryPointSet = new Set<EntryPoint>();

  for (const course of courses) {
    if (
      course.intakeId === intakeId &&
      course.level === studyLevel &&
      course.name === courseName &&
      course.mode === mode &&
      course.campus === campus
    ) {
      const isAvailable = effectiveFeeStatus === FeeStatus.Home ? course.availableHome : course.availableOverseas;
      if (isAvailable) {
        entryPointSet.add(course.entryPoint);
      }
    }
  }

  return Array.from(entryPointSet)
    .map(entryPoint => ({
      value: entryPoint,
      label: entryPoint,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Finds the course ID for a specific combination of criteria
 */
export const findCourseIdForCriteria = (
  courses: IApplyCourse[],
  intakeId: string,
  feeStatus: FeeStatus | string,
  studyLevel: StudyLevel | string,
  courseName: string,
  mode: string,
  campus: string,
  entryPoint: EntryPoint | string,
): string | null => {
  const effectiveFeeStatus = feeStatus === FeeStatus.NotSure ? FeeStatus.Overseas : feeStatus;

  for (const course of courses) {
    if (
      course.intakeId === intakeId &&
      course.level === studyLevel &&
      course.name === courseName &&
      course.mode === mode &&
      course.campus === campus &&
      course.entryPoint === entryPoint
    ) {
      const isAvailable = effectiveFeeStatus === FeeStatus.Home ? course.availableHome : course.availableOverseas;
      if (isAvailable) {
        return course.id;
      }
    }
  }

  return null;
};

/**
 * Finds the course ID for staff course selection (without fee status or study level filtering)
 */
export const findCourseIdForStaffSelection = (
  courses: IApplyCourse[],
  intakeId: string,
  courseName: string,
  mode: string,
  campus: string,
  entryPoint: EntryPoint | string,
): string | null => {
  const found = courses.find(
    course =>
      course.intakeId === intakeId &&
      course.name === courseName &&
      course.mode === mode &&
      course.campus === campus &&
      course.entryPoint === entryPoint,
  );

  return found?.id ?? null;
};

/**
 * Auto-selects a single option value if there's only one option available
 */
export const getAutoSelectedSingleOptionValue = (
  hasPrerequisites: boolean,
  options: DropdownOption[],
  currentValue?: string,
): string | undefined => {
  if (!hasPrerequisites || options.length !== 1) {
    return undefined;
  }

  const [onlyOption] = options;
  return currentValue === onlyOption.value ? undefined : onlyOption.value;
};

/**
 * Extracts error message from agent registration error
 */
export const extractErrorMessage = (error: unknown): string | null => {
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'error' in error) {
    const err = error as { error: unknown };
    if (typeof err.error === 'string') return err.error;
  }
  return null;
};
